import { useCallback, useEffect, useState } from 'react'
import {
  adminSessionStore,
  errorMessage,
  getTeam,
  listSettings,
  listTeams,
  moveTeamMember,
  reshuffleTeams,
} from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { LEVELS, LEVEL_FA } from './constants'
import { formatDatetime, formatDayCompact } from './dates'
import {
  JOIN_REASON_FA,
  describeBucket,
  describeOutcome,
  isFull,
  isPeriodKey,
  periodLabel,
  shiftPeriod,
  summaryOf,
  teamRefusal,
} from './teams'

// ─── Teams tab (admin-teams, V4-p) ────────────────────────────────────────────

const inputCls = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:bg-slate-800'
const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40'
const card = 'rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:bg-slate-800'
const errBox = 'rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700'
const TONE_CLS = {
  ok: 'bg-green-50 text-green-800',
  info: 'bg-sky-50 text-sky-800',
  warn: 'bg-amber-50 text-amber-900',
}

/** The setting that names the minutes bands — read once so a team's band shows as «۱۱–۲۰ دقیقه», not an index. */
const BUCKETS_KEY = 'teams.minutesBuckets'

function bucketsFrom(settings) {
  const row = (settings ?? []).find(item => item.key === BUCKETS_KEY)
  return Array.isArray(row?.value) ? row.value : null
}

/** The teams of one period, one row each; the open one is highlighted. */
export function TeamsTable({ data, buckets, openId, onOpen }) {
  if (data.items.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-slate-400">
        برای این دوره و سطح هنوز تیمی ساخته نشده است. با «اجرای خشک تعویض» ببینید تیم‌بندی چه خواهد ساخت.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:bg-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500">
          <tr>
            <th className="px-3 py-2 text-start font-medium">#</th>
            <th className="px-3 py-2 text-start font-medium">نام تیم</th>
            <th className="px-3 py-2 text-start font-medium">سطح</th>
            <th className="px-3 py-2 text-start font-medium">سطل دقیقه</th>
            <th className="px-3 py-2 text-start font-medium">اعضا</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {data.items.map(team => {
            const full = isFull(team, data.maxSize)
            return (
              <tr key={team.id} className={`border-t border-slate-100 ${team.id === openId ? 'bg-brand-50' : ''}`}>
                <td className="px-3 py-2 text-xs text-slate-400">{faDigits(team.position + 1)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{team.name}</div>
                  <div className="text-xs text-slate-400" dir="ltr">{team.nameEn}</div>
                </td>
                <td className="px-3 py-2 text-slate-600">{LEVEL_FA[team.level] ?? team.level}</td>
                <td className="px-3 py-2 text-slate-600">{describeBucket(buckets, team.minutesBucket)}</td>
                <td className="px-3 py-2">
                  <span className="text-slate-700">{faDigits(team.memberCount)} / {faDigits(data.maxSize)}</span>
                  {full && <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">پر</span>}
                </td>
                <td className="px-3 py-2 text-end">
                  <button type="button" onClick={() => onOpen(team.id)} className={btnGhost}>
                    {team.id === openId ? 'بستن' : 'اعضا'}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** One team's members: level, daily goal, XP, streak, last practice and how they joined — each with a move button. */
export function TeamMembers({ team, buckets, onMove, onClose }) {
  return (
    <section className={card}>
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-sm font-semibold text-slate-800">{team.name}</h3>
        <span className="text-xs text-slate-400" dir="ltr">{team.nameEn}</span>
        <span className="text-xs text-slate-500">{LEVEL_FA[team.level] ?? team.level} · {describeBucket(buckets, team.minutesBucket)} · {periodLabel(team.periodKey)}</span>
        <button type="button" onClick={onClose} className={`ms-auto ${btnGhost}`}>بستن</button>
      </div>
      {team.members.length === 0
        ? <p className="mt-3 text-xs text-slate-400">این تیم عضوی ندارد.</p>
        : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-start font-medium">نام نمایشی</th>
                  <th className="px-2 py-1.5 text-start font-medium">سطح</th>
                  <th className="px-2 py-1.5 text-start font-medium">هدف روزانه</th>
                  <th className="px-2 py-1.5 text-start font-medium">XP</th>
                  <th className="px-2 py-1.5 text-start font-medium">استریک</th>
                  <th className="px-2 py-1.5 text-start font-medium">آخرین تمرین</th>
                  <th className="px-2 py-1.5 text-start font-medium">ورود به تیم</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {team.members.map(member => (
                  <tr key={member.userId} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-slate-800">{member.displayName ?? <span className="text-slate-400">بی‌نام</span>}</td>
                    <td className="px-2 py-1.5 text-slate-600">{LEVEL_FA[member.level] ?? member.level}</td>
                    <td className="px-2 py-1.5 text-slate-600">{member.dailyMinutes == null ? '—' : `${faDigits(member.dailyMinutes)} دقیقه`}</td>
                    <td className="px-2 py-1.5 text-slate-600">{faDigits(member.xp)}</td>
                    <td className="px-2 py-1.5 text-slate-600">{faDigits(member.streakDays)} روز</td>
                    <td className="px-2 py-1.5 text-slate-600">{formatDayCompact(member.lastPracticedOn)}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-500">
                      <div>{JOIN_REASON_FA[member.reason] ?? member.reason}</div>
                      <div>{formatDatetime(member.joinedAt)}</div>
                    </td>
                    <td className="px-2 py-1.5 text-end">
                      <button type="button" onClick={() => onMove(member)} className={btnGhost}>جابه‌جایی</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  )
}

/**
 * Move one learner into another team of the same period. Every team of the
 * period is offered (any level — the server allows it and an admin may mean
 * it), the full ones disabled. The list was read when the dialog opened, so
 * a seat can still be gone by the time of the click: the server's 409
 * `team_full` is shown in Persian and the dialog stays open.
 */
export function MoveDialog({ member, fromTeam, targets, maxSize, jwtSignedIn, onMoved, onCancel }) {
  const [toTeamId, setToTeamId] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const options = (targets ?? []).filter(team => team.id !== fromTeam.id)

  async function submit(e) {
    e.preventDefault()
    if (!toTeamId) { setErr('تیم مقصد را انتخاب کنید.'); return }
    setBusy(true); setErr('')
    try {
      const target = await moveTeamMember({ userId: member.userId, toTeamId, reason: reason.trim() || undefined })
      onMoved(target)
    } catch (ex) {
      setErr(teamRefusal(ex) ?? errorMessage(ex))
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="team-move-title">
      <form onSubmit={submit} className="w-full max-w-md space-y-3 rounded-xl bg-white p-4 shadow-lg dark:bg-slate-800">
        <h3 id="team-move-title" className="text-sm font-semibold text-slate-800">
          جابه‌جایی «{member.displayName ?? 'بی‌نام'}» از {fromTeam.name}
        </h3>
        {!jwtSignedIn && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            با کلید مشترک وارد شده‌اید؛ جابه‌جایی فقط با ورود از طریق شماره تلفن ممکن است.
          </p>
        )}
        {targets === null
          ? <p className="text-xs text-slate-400">در حال خواندن تیم‌های این دوره…</p>
          : options.length === 0
            ? <p className="text-xs text-slate-500">تیم دیگری در این دوره نیست.</p>
            : (
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">تیم مقصد ({periodLabel(fromTeam.periodKey)})</span>
                <select value={toTeamId} onChange={e => setToTeamId(e.target.value)} className={`w-full ${inputCls}`}>
                  <option value="">انتخاب کنید…</option>
                  {options.map(team => {
                    const full = isFull(team, maxSize)
                    return (
                      <option key={team.id} value={team.id} disabled={full}>
                        {team.name} — {LEVEL_FA[team.level] ?? team.level} — {faDigits(team.memberCount)}/{faDigits(maxSize)}{full ? ' (پر)' : ''}
                      </option>
                    )
                  })}
                </select>
              </label>
            )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">دلیل (اختیاری، در گزارش ممیزی ثبت می‌شود)</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500} className={`w-full ${inputCls}`} />
        </label>
        {err && <div className={errBox}>{err}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={busy || !toTeamId} className={btnPrimary}>{busy ? 'در حال ثبت…' : 'جابه‌جا کن'}</button>
          <button type="button" disabled={busy} onClick={onCancel} className={btnGhost}>انصراف</button>
        </div>
      </form>
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-base font-semibold text-slate-800">{faDigits(value ?? 0)}</div>
    </div>
  )
}

/** A reshuffle summary: the counts, each level's share, the undersized teams and — on a dry run — every planned team. */
export function ReshuffleSummary({ summary, buckets }) {
  const planned = summary.plannedTeams ?? []
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <Stat label="نامزدها" value={summary.candidates} />
        <Stat label="جای‌گرفته" value={summary.assigned} />
        <Stat label="تیم تازه" value={summary.teams} />
        <Stat label="از قبل در تیم" value={summary.alreadyAssigned} />
        <Stat label="کنار گذاشته (بی‌فعالیت)" value={summary.inactive} />
        <Stat label="تیم کوچک‌تر" value={summary.undersized?.length} />
      </div>
      {summary.byLevel && (
        <ul className="flex flex-wrap gap-3 text-xs text-slate-600">
          {LEVELS.filter(level => summary.byLevel[level]).map(level => (
            <li key={level}>
              {LEVEL_FA[level]}: {faDigits(summary.byLevel[level].learners)} نفر در {faDigits(summary.byLevel[level].teams)} تیم
            </li>
          ))}
        </ul>
      )}
      {summary.undersized?.length > 0 && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          تیم‌های کوچک‌تر از حد (یک سطح نفر کم آورده):{' '}
          {summary.undersized.map(item => `${LEVEL_FA[item.level] ?? item.level} · ${describeBucket(buckets, item.minutesBucket)} · ${faDigits(item.size)} نفر`).join('، ')}
        </div>
      )}
      {planned.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-start font-medium">#</th>
                <th className="px-2 py-1.5 text-start font-medium">تیم برنامه‌ریزی‌شده</th>
                <th className="px-2 py-1.5 text-start font-medium">سطح</th>
                <th className="px-2 py-1.5 text-start font-medium">سطل دقیقه</th>
                <th className="px-2 py-1.5 text-start font-medium">اندازه</th>
              </tr>
            </thead>
            <tbody>
              {planned.map(team => (
                <tr key={team.position} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 text-xs text-slate-400">{faDigits(team.position + 1)}</td>
                  <td className="px-2 py-1.5 text-slate-800">{team.name}</td>
                  <td className="px-2 py-1.5 text-slate-600">{LEVEL_FA[team.level] ?? team.level}</td>
                  <td className="px-2 py-1.5 text-slate-600">{describeBucket(buckets, team.minutesBucket)}</td>
                  <td className="px-2 py-1.5 text-slate-600">{faDigits(team.size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * «اجرای خشک تعویض» and, only after it, the real run. The real button does
 * not exist until a dry run of this very period has been shown, and it asks
 * for the period key typed back before it is enabled — building a month
 * writes a membership for every active learner and cannot be undone from the
 * panel. Changing the period drops the dry run, so its summary can never be
 * mistaken for another month's.
 */
export function ReshufflePanel({ periodKey, buckets, onRan, initialResult = null }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(initialResult)
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')

  useEffect(() => { setResult(initialResult); setConfirming(false); setTyped(''); setErr('') }, [periodKey, initialResult])

  const dryDone = result?.outcome === 'dry-run'
  const summary = summaryOf(result)
  const outcome = result ? describeOutcome(result) : null

  async function run(dryRun) {
    setBusy(true); setErr('')
    try {
      const answer = await reshuffleTeams({ periodKey, dryRun })
      setResult(answer)
      setConfirming(false); setTyped('')
      if (!dryRun) onRan(answer)
    } catch (ex) {
      setErr(errorMessage(ex))
    } finally { setBusy(false) }
  }

  return (
    <section className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-700">تعویض تیم‌ها — {periodLabel(periodKey)}</h3>
        <button type="button" disabled={busy || !isPeriodKey(periodKey)} onClick={() => run(true)} className={`ms-auto ${btnGhost}`}>
          {busy && !confirming ? 'در حال اجرا…' : 'اجرای خشک تعویض'}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        اجرای خشک نشان می‌دهد تیم‌بندی این دوره چه تیم‌هایی می‌سازد و هیچ چیزی نمی‌نویسد. اجرای واقعی فقط پس از اجرای خشک همین دوره در دسترس است.
      </p>
      {err && <div className={errBox}>{err}</div>}
      {outcome && <div className={`rounded-lg px-3 py-2 text-sm ${TONE_CLS[outcome.tone]}`}>{outcome.text}</div>}
      {summary && <ReshuffleSummary summary={summary} buckets={buckets} />}

      {dryDone && !confirming && (
        <button type="button" disabled={busy} onClick={() => setConfirming(true)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
          اجرای واقعی تیم‌بندی…
        </button>
      )}
      {dryDone && confirming && (
        <div className="space-y-2 rounded-lg bg-red-50 px-3 py-3 text-sm text-red-800">
          <p>
            تیم‌بندی واقعی برای هر زبان‌آموز فعالِ این دوره عضویت می‌نویسد و از پنل برگشت‌پذیر نیست. برای تأیید، کلید دوره
            (<span dir="ltr" className="font-mono">{periodKey}</span>) را بنویسید.
          </p>
          <input type="text" value={typed} onChange={e => setTyped(e.target.value)} dir="ltr" placeholder={periodKey} className={`w-40 ${inputCls}`} aria-label="کلید دوره برای تأیید" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy || typed.trim() !== periodKey} onClick={() => run(false)}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {busy ? 'در حال اجرا…' : 'بله، تیم‌بندی کن'}
            </button>
            <button type="button" disabled={busy} onClick={() => { setConfirming(false); setTyped('') }} className={btnGhost}>انصراف</button>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * The «تیم‌ها» tab (V4-p): pick a period (shamsi month) and a level, see the
 * teams with their member counts, open one for its members, move a learner
 * by hand, and dry-run the monthly reshuffle. Everything here works whether
 * or not `teams.enabled` is on — that is the point: an admin inspects teams
 * before learners ever see them.
 */
export function TeamsTab() {
  const [periodKey, setPeriodKey] = useState('') // '' = the server's current month
  const [periodInput, setPeriodInput] = useState('')
  const [level, setLevel] = useState('')
  const [data, setData] = useState(null)
  const [settings, setSettings] = useState(null)
  const [err, setErr] = useState('')
  const [openTeam, setOpenTeam] = useState(null)
  const [detailErr, setDetailErr] = useState('')
  const [moving, setMoving] = useState(null) // the member whose dialog is open
  const [targets, setTargets] = useState(null)
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try {
      const answer = await listTeams({ periodKey, level })
      setData(answer)
      setPeriodInput(answer.periodKey)
    } catch (ex) { setErr(errorMessage(ex)) }
  }, [periodKey, level])

  useEffect(() => { setData(null); load() }, [load])
  useEffect(() => { listSettings().then(setSettings).catch(() => setSettings([])) }, [])

  const buckets = bucketsFrom(settings)
  const shownPeriod = data?.periodKey ?? periodKey
  const jwtSignedIn = Boolean(adminSessionStore.access)

  function goToPeriod(key) {
    setOpenTeam(null); setNotice('')
    setPeriodKey(key)
  }

  function applyPeriodInput(e) {
    e.preventDefault()
    const key = periodInput.trim()
    if (!isPeriodKey(key)) { setErr('دوره باید به شکل YYYY-MM باشد، مثلاً 1405-07.'); return }
    goToPeriod(key)
  }

  async function openDetail(teamId) {
    if (openTeam?.id === teamId) { setOpenTeam(null); return }
    setDetailErr(''); setNotice('')
    try { setOpenTeam(await getTeam(teamId)) }
    catch (ex) { setDetailErr(teamRefusal(ex) ?? errorMessage(ex)) }
  }

  async function startMove(member) {
    setMoving(member); setTargets(null)
    try {
      const all = await listTeams({ periodKey: openTeam.periodKey })
      setTargets(all.items)
    } catch (ex) {
      setTargets([]); setDetailErr(errorMessage(ex))
    }
  }

  async function moved(target) {
    setNotice(`«${moving.displayName ?? 'بی‌نام'}» به ${target.name} منتقل شد.`)
    setMoving(null)
    setOpenTeam(target)
    await load()
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600">دوره</span>
        <button type="button" disabled={!isPeriodKey(shownPeriod)} onClick={() => goToPeriod(shiftPeriod(shownPeriod, -1))} className={btnGhost} aria-label="ماه قبل">‹</button>
        <form onSubmit={applyPeriodInput} className="flex items-center gap-1">
          <input type="text" value={periodInput} onChange={e => setPeriodInput(e.target.value)} dir="ltr" placeholder="1405-07" className={`w-28 ${inputCls}`} aria-label="کلید دوره" />
          <button type="submit" className={btnGhost}>برو</button>
        </form>
        <button type="button" disabled={!isPeriodKey(shownPeriod)} onClick={() => goToPeriod(shiftPeriod(shownPeriod, 1))} className={btnGhost} aria-label="ماه بعد">›</button>
        <span className="text-sm font-medium text-slate-700">{periodLabel(shownPeriod)}</span>
        {periodKey && <button type="button" onClick={() => goToPeriod('')} className="text-xs text-brand-600 hover:underline">ماه جاری</button>}

        <span className="ms-4 text-sm text-slate-600">سطح</span>
        {['', ...LEVELS].map(one => (
          <button
            key={one || 'all'}
            type="button"
            onClick={() => { setOpenTeam(null); setLevel(one) }}
            className={`rounded-lg px-3 py-1.5 text-sm ${one === level ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            {one ? LEVEL_FA[one] : 'همه'}
          </button>
        ))}
        <button type="button" onClick={load} className={`ms-auto ${btnGhost}`}>تازه‌سازی</button>
      </div>

      {err && <div className={errBox}>{err}</div>}
      {notice && <div className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700">{notice}</div>}

      {data === null
        ? (!err && <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>)
        : <TeamsTable data={data} buckets={buckets} openId={openTeam?.id} onOpen={openDetail} />}

      {detailErr && <div className={errBox}>{detailErr}</div>}
      {openTeam && <TeamMembers team={openTeam} buckets={buckets} onMove={startMove} onClose={() => setOpenTeam(null)} />}

      {isPeriodKey(shownPeriod) && <ReshufflePanel periodKey={shownPeriod} buckets={buckets} onRan={load} />}

      {moving && openTeam && (
        <MoveDialog
          member={moving}
          fromTeam={openTeam}
          targets={targets}
          maxSize={data?.maxSize}
          jwtSignedIn={jwtSignedIn}
          onMoved={moved}
          onCancel={() => setMoving(null)}
        />
      )}
    </div>
  )
}
